import { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SingleFilterContainer } from '~/components/Filters/SingleFilters/SingleFilterContainer';
import { TagsFilter } from '~/components/Filters/TagsFilter/TagsFilter';
import { type SingleFilterProps } from '~/components/Filters/types';
import { PrivatePopularTags, type PrivatePopularTagsProps } from '~/components/Tags/PrivatePopularTags';
import { PrivateTagsSelect } from '~/components/Tags/PrivateTagsSelect';
import { type TagBase } from '~/components/Tags/utils';
import {
  clearTagsFilterValuesAction,
  setTagsFilterLogicAction,
  setTagsFiltersValueAction,
} from '~/store/filters/actions';
import {
  hasTagsFiltersValuesSelector,
  selectedTagsFilterLogicSelector,
  selectedTagsFiltersSelector,
} from '~/store/filters/selectors';
import { type FilterLogicType } from '~/store/filters/types';
import { useAirSelector } from '~/utils/ReduxUtils';

export const SingleTagFilter = memo(({ onApplyClick }: SingleFilterProps) => {
  const dispatch = useDispatch();
  const hasValues = useAirSelector(hasTagsFiltersValuesSelector);
  const clearValues = useCallback(() => {
    dispatch(clearTagsFilterValuesAction());
  }, [dispatch]);

  const selectedLogic = useSelector(selectedTagsFilterLogicSelector);
  const selectedTags = useSelector(selectedTagsFiltersSelector);

  const onLogicChange = useCallback(
    (logic: FilterLogicType) => dispatch(setTagsFilterLogicAction({ logic })),
    [dispatch],
  );

  const onSelectedTagsChanged = useCallback(
    (tags: TagBase[]) => {
      dispatch(setTagsFiltersValueAction({ tags }));
      onApplyClick();
    },
    [dispatch, onApplyClick],
  );

  const onTagClick = useCallback<PrivatePopularTagsProps['onTagClick']>(
    (tag) => {
      dispatch(setTagsFiltersValueAction({ tags: [tag] }));
      onApplyClick();
    },
    [dispatch, onApplyClick],
  );

  return (
    <SingleFilterContainer
      hasValues={hasValues}
      currentFilter="tags"
      onClearClick={clearValues}
      onApplyClick={onApplyClick}
    >
      <TagsFilter
        TagsSelect={PrivateTagsSelect}
        selectedLogic={selectedLogic}
        selectedTags={selectedTags}
        onLogicChange={onLogicChange}
        onSelectedTagsChanged={onSelectedTagsChanged}
      />
      {!selectedTags.length && <PrivatePopularTags className="mt-3" onTagClick={onTagClick} />}
    </SingleFilterContainer>
  );
});

SingleTagFilter.displayName = 'SingleTagFilter';
